/**
 * ★★ WO — O PAINEL DE UM DIA, POR PESSOA. Puro: nenhum Firestore aqui dentro.
 *
 * Recebe as interações já recortadas pelo dia (quem monta a faixa UTC é o
 * `produtividadeService`, com `faixaDoDiaNaClinica`) e devolve uma linha por pessoa.
 *
 * ★★ O QUE ESTE MÓDULO NÃO FAZ, e cada "não" veio de uma medição:
 *
 *  1. ★★ **NÃO calcula taxa nenhuma.** Medido (`wo-medicao.md` §13): os degraus de setembro
 *     tinham 11, 9, 3 e 1 evento. A taxa da Talita era `1 de 4 = 25%`; **um caso a mais a
 *     levaria a 50%** sem ela fazer nada diferente. Taxa no dia é ruído. As taxas moram em
 *     `acumulado.js`, com janela longa e selo de base pequena. Há guarda de fonte (T10).
 *  2. ★★ **NÃO lê `lead.tentativasContato`.** Medido: o contador soma **55** e a lista tem
 *     **54** — já divergem. E ele é calculado no cliente, sem `increment()`. A fonte é a
 *     LISTA (`interacoes` tipo `ligacao`), que é append-only por rules.
 *  3. ★ **NÃO conta cliques como unidade principal.** `leadsTentados` (distintos) é o número
 *     que a tela destaca; `tentativas` fica ao lado. ★ É o que torna o clique repetido
 *     inofensivo **sem precisar de trava de cadência** — e medido: **zero** leads com mais de
 *     5 tentativas no mesmo dia.
 *  4. ★ **NÃO reescreve o comparecimento.** Importa `comparecimentosNoPeriodo`, a regra única
 *     que o dashboard e a comissão já usam.
 */

import {
  comparecimentosNoPeriodo,
  creditoComparecimento,
  indexarAgendamentosPorLead,
  indexarEquipe,
} from '@/leads/contagemComparecimento';
import { creditoNegociador } from '@/metas/credito';
import { estaPendente, temBaixa } from '@/agenda/pendencia';
import { RESULTADOS_LIGACAO } from '@/lib/constants';
import { temFuncao } from '@/equipe/funcoes';
import { canonicalTelefone, normalizarTelefone, variantesTelefone } from '@/lib/telefone';

/** `Timestamp | Date | string` → `Date`, ou null. */
export const paraData = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * ★★ Indicadores (01/11/2026) — OS FORMATADORES DO FUSO SÃO CRIADOS UMA VEZ, aqui.
 *
 * ⚠ Medido com o volume de Campinas (8.884 leads, 20.638 interações): `diaDaData` e
 * `mesNaClinica` criavam um `Intl.DateTimeFormat` POR CHAMADA — ≈ 120 mil por abertura da
 * tela —, e isso era a maior parte dos 3,2 s de cálculo da grade. ★ O formatador é o mesmo
 * para qualquer data; reaproveitá-lo não muda a saída (teste de igualdade em
 * `indicadores.igualdade.test.js`).
 */
const FORMATO_MES = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit',
});
const FORMATO_DIA = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
});

/** 'AAAA-MM' de um instante, no fuso da clínica. */
const mesNaClinica = (data) => FORMATO_MES.format(data).slice(0, 7);

/** As etapas em que um lead está "esperando o negociador". */
export const ETAPAS_DE_FOLLOW_UP = ['compareceu', 'nao_fechou'];

/** Só dígitos, para comparar telefone sem depender de máscara. */
const soDigitos = (t) => String(t ?? '').replace(/\D/g, '');

/**
 * ★★ O RESULTADO da tentativa: do CAMPO, com reserva no TEXTO.
 *
 * O campo `resultado` existe desde o lote 1. ⚠ As **54** tentativas de setembro e tudo que
 * veio antes só têm o resultado **dentro da frase** (`"Tentativa de contato — atendeu."`), e
 * o carimbo não o recuperou (inventá-lo seria reconstrução).
 *
 * ★ A reserva lê o texto; se nenhum resultado conhecido aparecer, devolve null — e a linha
 * conta em `tentativas` mas em nenhum balde de resultado. **Não se adivinha "atendeu".**
 */
export function resultadoDaTentativa(interacao) {
  const campo = String(interacao?.resultado ?? '').trim();
  if (RESULTADOS_LIGACAO.includes(campo)) return campo;
  const texto = String(interacao?.texto ?? '');
  // ★ Os mais longos primeiro: "não atende" não pode ser achado dentro de outro rótulo.
  for (const r of [...RESULTADOS_LIGACAO].sort((a, b) => b.length - a.length)) {
    if (texto.includes(r)) return r;
  }
  return null;
}

/**
 * ★ A etapa do lead NO MOMENTO da interação, com reserva na etapa de hoje.
 *
 * `etapaNoMomento` existe desde o lote 1. Para as **11.090** interações antigas o campo está
 * ausente, e aí só resta a etapa ATUAL — que é aproximação, não o fato.
 *
 * @returns `{ etapa, aproximada }` — a tela mostra o aviso quando `aproximada` é true.
 */
export function etapaDaInteracao(interacao, lead) {
  const nomomento = interacao?.etapaNoMomento;
  if (typeof nomomento === 'string' && nomomento !== '') return { etapa: nomomento, aproximada: false };
  return { etapa: lead?.etapa ?? null, aproximada: true };
}

const LINHA_VAZIA = {
  tentativas: 0, leadsTentados: 0,
  atendidas: 0, naoAtende: 0, caixaPostal: 0, numeroErrado: 0, semResultado: 0,
  reativacoes: 0,
  agendou: 0, agendouSemBaixa: 0,
  compareceu: 0, fechou: 0, valor: 0,
  followUps: 0, followUpsAproximados: 0,
  proximosContatosCriados: 0, reagendamentos: 0,
};

const empurrar = (mapa, chave, item) => {
  if (!mapa.has(chave)) mapa.set(chave, []);
  mapa.get(chave).push(item);
};

/**
 * ★★ Indicadores (01/11/2026) — O ÍNDICE DO PERÍODO: o que `painelDoDia` precisa dos leads e
 * dos agendamentos, montado UMA vez.
 *
 * ⚠ Antes, cada dia da grade refazia tudo isto: o `Map` dos leads, os comparecimentos de
 * TODOS os leads e o laço dos fechados — 27 passagens pelos 8.884 leads de Campinas por
 * abertura, e de novo a cada lead alterado. ★ Agora é uma passagem, e cada dia só lê o seu
 * balde.
 *
 * ★★ A REGRA NÃO MUDA DE LUGAR: o comparecimento continua saindo de `comparecimentosNoPeriodo`
 * (a regra única da WK) e o crédito de `creditoComparecimento`, lá em `painelDoDia`. O índice
 * só agrupa por dia, no MESMO fuso (`diaDaData`) e na MESMA ordem dos arrays, porque a ordem
 * decide a ordem das linhas.
 */
export function indexarPainel({
  leads = [],
  agendamentos = [],
  equipe = [],
  telefonesEquipe = [],
} = {}) {
  const leadPorId = new Map((leads ?? []).map((l) => [l.id, l]));
  const idsDaEquipe = new Set((equipe ?? []).map((u) => u.id));
  /**
   * ★★ EXCLUI A PRÓPRIA EQUIPE, e isto não é detalhe.
   *
   * Medido (`wo-medicao.md` §3): um "contato ativo" da Jéssica era **o próprio dono** —
   * conversa interna de teste. Sem o filtro, teste conta como produtividade.
   */
  /**
   * ⚠⚠ DEFEITO MEU DO LOTE 2, CONSERTADO AQUI — e ele teria falhado EM SILÊNCIO.
   *
   * A primeira versão comparava `soDigitos(a) === soDigitos(b)`. ★ O lead guarda o telefone
   * na forma CANÔNICA (`+5562996640861`) e o gestor digita `62996640861` ou
   * `(62) 99664-0861` — os dígitos **não batem** por causa do `55`, e a trava anti-burla
   * simplesmente não excluiria ninguém. ⚠ Como a lista esteve VAZIA em produção até o lote
   * 3a, o defeito nunca apareceu: ele estrearia no dia em que o gestor a preenchesse.
   *
   * ★ `variantesTelefone` é o que a casa já usa para este problema (o nono dígito e o
   * prefixo), e o `canonicalTelefone` normaliza o lado do lead. ⚠ Não há terceira régua de
   * telefone aqui — seria a quarta da casa.
   */
  const telsDaEquipe = new Set();
  for (const bruto of telefonesEquipe ?? []) {
    const e164 = normalizarTelefone(bruto);
    for (const v of variantesTelefone(e164 || bruto)) telsDaEquipe.add(soDigitos(v));
    if (soDigitos(bruto)) telsDaEquipe.add(soDigitos(bruto));
  }
  const ehDaEquipe = (lead) => {
    if (!lead?.telefone || telsDaEquipe.size === 0) return false;
    return variantesTelefone(canonicalTelefone(lead.telefone))
      .some((v) => telsDaEquipe.has(soDigitos(v)));
  };


  const agendadosPorDia = new Map();
  for (const a of agendamentos ?? []) {
    const quando = paraData(a?.criadoEm);
    if (quando) empurrar(agendadosPorDia, diaDaData(quando), a);
  }
  const comparecimentosPorDia = new Map();
  for (const { lead, quando } of comparecimentosNoPeriodo(leads, agendamentos)) {
    empurrar(comparecimentosPorDia, diaDaData(quando), lead);
  }
  const fechadosPorDia = new Map();
  for (const l of leads ?? []) {
    if (l?.etapa !== 'fechado') continue;
    const quando = paraData(l.dataFechamento);
    if (quando) empurrar(fechadosPorDia, diaDaData(quando), l);
  }

  return {
    leadPorId,
    idsDaEquipe,
    ehDaEquipe,
    daEquipe: indexarEquipe(equipe),
    agsPorLead: indexarAgendamentosPorLead(agendamentos),
    agendadosPorDia,
    comparecimentosPorDia,
    fechadosPorDia,
  };
}

/**
 * O painel de um dia.
 *
 * @param dia            'AAAA-MM-DD' no fuso da clínica — só para carimbar a saída
 * @param interacoes     as interações JÁ recortadas pelo dia
 * @param leads          os leads da clínica (para crédito, safra e etapa de reserva)
 * @param agendamentos   os agendamentos (para agendou/compareceu)
 * @param equipe         `[{ id, nome, funcoes, ativo }]`
 * @param telefonesEquipe telefones da própria equipe, a excluir
 * @param agora          instante de referência para "pendente"
 * @param indice         ★ o de `indexarPainel`, quando quem chama monta vários dias com os
 *                       MESMOS leads e agendamentos (`painelPorDia`); sem ele, monta-se aqui
 */
export function painelDoDia({
  dia,
  interacoes = [],
  leads = [],
  agendamentos = [],
  equipe = [],
  telefonesEquipe = [],
  agora = new Date(),
  indice = null,
} = {}) {
  const {
    leadPorId, idsDaEquipe, ehDaEquipe, daEquipe, agsPorLead,
    agendadosPorDia, comparecimentosPorDia, fechadosPorDia,
  } = indice ?? indexarPainel({ leads, agendamentos, equipe, telefonesEquipe });
  const porPessoa = new Map();
  const garantir = ({ id, nome }) => {
    if (!porPessoa.has(id)) porPessoa.set(id, { id, nome, ...LINHA_VAZIA, leadsTentadosIds: new Set() });
    return porPessoa.get(id);
  };
  // Toda pessoa com função de venda aparece, mesmo com o dia zerado: linha ausente esconde
  // justamente quem precisa vê-la.
  for (const u of equipe ?? []) {
    if (temFuncao(u, 'sdr') || temFuncao(u, 'negociador')) garantir({ id: u.id, nome: u.nome ?? '' });
  }

  const mesDoDia = String(dia ?? '').slice(0, 7);

  for (const i of interacoes ?? []) {
    // ★ Só interação com AUTOR real entra. 'sistema' é o webhook, e não é de ninguém.
    if (!i?.autorId || !idsDaEquipe.has(i.autorId)) continue;
    const lead = leadPorId.get(i.leadId);
    if (ehDaEquipe(lead)) continue;
    const pessoa = garantir({ id: i.autorId, nome: i.autorNome ?? '' });

    if (i.tipo === 'ligacao') {
      pessoa.tentativas += 1;
      if (i.leadId) pessoa.leadsTentadosIds.add(i.leadId);
      const r = resultadoDaTentativa(i);
      if (r === 'atendeu') pessoa.atendidas += 1;
      else if (r === 'não atende') pessoa.naoAtende += 1;
      else if (r === 'caixa postal') pessoa.caixaPostal += 1;
      else if (r === 'número errado') pessoa.numeroErrado += 1;
      else pessoa.semResultado += 1;

      // ★ REATIVAÇÃO: o lead entrou num mês ANTERIOR ao do dia.
      const entrou = paraData(lead?.criadoEm);
      if (entrou && mesDoDia && mesNaClinica(entrou) < mesDoDia) pessoa.reativacoes += 1;
    }

    if (i.tipo === 'tarefa') {
      // ★ O texto distingue os três gestos de tarefa; é o que o `tarefasService` grava.
      if (/reagendado/i.test(String(i.texto ?? ''))) pessoa.reagendamentos += 1;
      else if (/agendado/i.test(String(i.texto ?? ''))) pessoa.proximosContatosCriados += 1;
    }

    /**
     * ★ FOLLOW-UP (definição D2 do dono): QUALQUER registro num lead que não fechou.
     * Pela etapa NO MOMENTO; sem o campo, pela de hoje — e aí é aproximação, contada aparte.
     */
    const { etapa, aproximada } = etapaDaInteracao(i, lead);
    if (ETAPAS_DE_FOLLOW_UP.includes(etapa)) {
      pessoa.followUps += 1;
      if (aproximada) pessoa.followUpsAproximados += 1;
    }
  }

  // ---- agendou / agendou sem baixa ----
  for (const a of (dia ? agendadosPorDia.get(dia) : null) ?? []) {
    const autor = a.agendadoPorId;
    if (!autor || !idsDaEquipe.has(autor)) continue;
    const lead = leadPorId.get(a.leadId);
    if (ehDaEquipe(lead)) continue;
    const pessoa = garantir({ id: autor, nome: a.agendadoPorNome ?? '' });
    pessoa.agendou += 1;
    // ⚠ `agendouSemBaixa` é INFORMAÇÃO, não punição: o lote 3 decide o que conta na meta.
    if (!temBaixa(a) && estaPendente(a, agora)) pessoa.agendouSemBaixa += 1;
  }

  /**
   * ---- compareceu (um por lead, pela regra única) ----
   *
   * ★★★ A-10.4 — O CRÉDITO É DE QUEM AGENDOU (`creditoComparecimento`), não mais do
   * SDR do lead (`creditoSdr`). ⚠⚠ Decisão do dono de 16/out/2026, e ela muda a
   * FOLHA: a mesma régua vale em `remuneracao/calculo.js` e no espelho do servidor.
   * ★ O rótulo da grade mudou junto — *"Comparecimentos (de quem agendou)"*.
   */
  for (const lead of (dia ? comparecimentosPorDia.get(dia) : null) ?? []) {
    if (ehDaEquipe(lead)) continue;
    const { id, nome } = creditoComparecimento(lead, agsPorLead.get(lead?.id) ?? [], daEquipe);
    if (!id) continue;
    garantir({ id, nome }).compareceu += 1;
  }

  // ---- fechou / valor (crédito do negociador) ----
  for (const l of (dia ? fechadosPorDia.get(dia) : null) ?? []) {
    if (ehDaEquipe(l)) continue;
    const { id, nome } = creditoNegociador(l);
    if (!id) continue;
    const pessoa = garantir({ id, nome });
    pessoa.fechou += 1;
    pessoa.valor += Number(l.valorFechado) || 0;
  }

  const linhas = [...porPessoa.values()].map(({ leadsTentadosIds, ...resto }) => ({
    ...resto,
    leadsTentados: leadsTentadosIds.size,
  }));

  const totais = linhas.reduce((t, l) => {
    for (const k of Object.keys(LINHA_VAZIA)) t[k] = (t[k] ?? 0) + (l[k] ?? 0);
    t.leadsTentados = (t.leadsTentados ?? 0) + l.leadsTentados;
    return t;
  }, {});

  return { dia, porPessoa: linhas, totais };
}

/** 'AAAA-MM-DD' de um instante, no fuso da clínica. */
export const diaDaData = (data) => (data ? FORMATO_DIA.format(data) : null);
